import React from 'react';
import * as R from 'ramda';
// components
import TextComponent from '../../../components/text';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// ui
import { Flex } from '../../../ui';
// utilities
import { sendRequest } from '../../../utilities/http';
import endpointsMap from '../../../utilities/endpoints';
// feature fleet-profile
import SimpleForm from '../forms';
//////////////////////////////////////////////////

const getListFromResponseData = (data) => {
  const itemList = R.pathOr(data, ['results'], data);

  if (G.isNilOrEmpty(itemList)) return [];

  return G.ifElse(G.isArray(itemList), itemList, R.of(Array, itemList));
};

const hasLimitedRemainingBalance = (item) => {
  const { limited, active, fullyPaid } = item;

  return active && limited && G.isFalse(fullyPaid);
};

const hasProcessRemainingBalance = (item) => R.and(
  hasLimitedRemainingBalance(item),
  G.isTrue(R.prop(GC.FIELD_PROCESS_REMAINING_BALANCE, item)),
);

const RemainingBalanceWarningsConfirm = ({ warnings, additionalText }) => {
  const messages = G.ifElse(
    G.isNotNilAndNotEmpty(additionalText),
    R.append(additionalText, warnings),
    warnings,
  );

  return (
    <Flex fontSize={18} flexDirection='column' alignItems='center'>
      {
        messages.map((message, index) => (
          <TextComponent
            key={index}
            textAlign='center'
            whiteSpace='normal'
            mb={G.ifElse(R.lt(index, R.dec(R.length(messages))), 10, 0)}
          >
            {message}
          </TextComponent>
        ))
      }
    </Flex>
  );
};

const fetchEntityList = async (endpointName, params) => {
  try {
    const endpoint = R.prop(endpointName, endpointsMap);

    if (G.isNilOrEmpty(endpoint)) return [];

    const res = await sendRequest('get', endpoint, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) return getListFromResponseData(data);

    return [];
  } catch (error) {
    G.handleException(error, 'fetchEntityList exception');

    return [];
  }
};

const fetchLimitedPayrollLists = async ({ guid, fleetType }) => {
  const isVendor = R.equals(fleetType, 'vendor');

  const primaryObjectGuidKey = G.ifElse(
    isVendor,
    GC.FIELD_FLEET_VENDOR_GUID,
    GC.FIELD_DRIVER_GUID,
  );

  const params = { [primaryObjectGuidKey]: guid };

  const [monthlyPaymentList, payrollAccessorialList] = await Promise.all([
    fetchEntityList(
      G.ifElse(isVendor, 'vendorMonthlyPaymentList', 'monthlyPaymentList'),
      params,
    ),
    fetchEntityList(
      G.ifElse(isVendor, 'fleetVendorPayrollAccessorialList', 'payrollAssessorialList'),
      params,
    ),
  ]);

  return { monthlyPaymentList, payrollAccessorialList };
};

const getEntityLabel = (fleetType) => G.getWindowLocale(...G.ifElse(
  R.equals(fleetType, 'vendor'),
  ['titles:vendor', 'Vendor'],
  ['titles:driver', 'Driver'],
));

const getDeactivationRemainingBalanceWarnings = async ({ guid, fleetType }) => {
  const { monthlyPaymentList, payrollAccessorialList } = await fetchLimitedPayrollLists({ guid, fleetType });

  const entityLabel = getEntityLabel(fleetType);

  const warnings = [];

  if (R.any(hasLimitedRemainingBalance, monthlyPaymentList)) {
    warnings.push(G.getWindowLocale(
      `messages:${entityLabel}-limited-monthly-payment-remaining-balance`,
      `This ${entityLabel} has an active Limited Monthly Payment with a remaining balance.`,
    ));
  }

  if (R.any(hasLimitedRemainingBalance, payrollAccessorialList)) {
    warnings.push(G.getWindowLocale(
      `messages:${entityLabel}-limited-payroll-accessorial-remaining-balance`,
      `This ${entityLabel} has an active Limited Payroll Accessorial with a remaining balance.`,
    ));
  }

  return warnings;
};

const getActivationProcessRemainingBalanceWarnings = async ({ guid, fleetType }) => {
  const { monthlyPaymentList, payrollAccessorialList } = await fetchLimitedPayrollLists({ guid, fleetType });

  const entityLabel = getEntityLabel(fleetType);

  const warnings = [];

  if (R.any(hasProcessRemainingBalance, monthlyPaymentList)) {
    warnings.push(G.getWindowLocale(
      `messages:${entityLabel}-limited-monthly-payment-process-remaining-balance`,
      `This ${entityLabel} has an active Limited Monthly Payment with Pay/Deduct Remaining Balance enabled.`,
    ));
  }

  if (R.any(hasProcessRemainingBalance, payrollAccessorialList)) {
    warnings.push(G.getWindowLocale(
      `messages:${entityLabel}-limited-payroll-accessorial-process-remaining-balance`,
      `This ${entityLabel} has an active Limited Payroll Accessorial with Pay/Deduct Remaining Balance enabled.`,
    ));
  }

  return warnings;
};

const getCreateOrUpdateItemModal = (props) => {
  const {
    fleetType,
    openModal,
    closeModal,
    CustomForm,
    openLoader,
    isEditMode,
    closeLoader,
    submitAction,
    itemTitleArr,
    fieldSettings,
    initialValues,
    isReadOnlyMode,
    fleetVendorGuid,
    validationSchema,
    primaryObjectGuid,
    fieldsWrapperStyles,
    makeOptionsForSelect,
    primaryObjectGuidKey,
    primaryObjectBranchGuid,
    modalAdditionalOptions = {},
  } = props;

  let optionsForSelect = {};

  if (G.isFunction(makeOptionsForSelect)) optionsForSelect = makeOptionsForSelect(props);

  let component;

  if (G.isNotNil(CustomForm)) {
    component = (
      <CustomForm
        fleetType={fleetType}
        openModal={openModal}
        openLoader={openLoader}
        closeModal={closeModal}
        isEditMode={isEditMode}
        closeLoader={closeLoader}
        submitAction={submitAction}
        initialValues={initialValues}
        isReadOnlyMode={isReadOnlyMode}
        fleetVendorGuid={fleetVendorGuid}
        optionsForSelect={optionsForSelect}
        primaryObjectGuid={primaryObjectGuid}
        primaryObjectGuidKey={primaryObjectGuidKey}
        primaryObjectBranchGuid={primaryObjectBranchGuid}
      />
    );
  } else {
    const fields = isReadOnlyMode ? R.map(R.assoc('disabled', true), fieldSettings) : fieldSettings;

    component = (
      <SimpleForm
        fieldSettings={fields}
        closeModal={closeModal}
        submitAction={submitAction}
        initialValues={initialValues}
        isReadOnlyMode={isReadOnlyMode}
        optionsForSelect={optionsForSelect}
        fieldsWrapperStyles={fieldsWrapperStyles}
        validationSchema={G.isFunction(validationSchema) ? validationSchema(props) : validationSchema}
      />
    );
  }

  let title;

  if (G.isNotNil(itemTitleArr)) title = G.ifElse(isEditMode, G.getEditTitle, G.getAddTitle)(itemTitleArr);

  return {
    p: 15,
    component,
    options: { title },
    ...modalAdditionalOptions,
  };
};

const getAllAllowedTabs = (tabs) => R.filter(
  R.compose(
    G.hasAmousCurrentUserPermissions,
    R.propOr(['ANY'], 'permissions'),
  ),
  tabs,
);

const getAllDefaultTabsValues = (allTabs) => R.pluck('tab', allTabs);

const assocName = R.map((item) => ({
  ...item,
  useCustomName: true,
  name: R.prop('text', item),
}));

const getTabsSettings = (allTabs) => R.indexBy(R.prop('tab'), assocName(allTabs));

const sortAndFilterTabs = (tabs, uiReportFields) => {
  if (G.isNilOrEmpty(uiReportFields)) return tabs;

  return R.pipe(
    R.filter((item) => R.includes(R.prop('tab', item), uiReportFields)),
    R.sortBy((item) => R.indexOf(R.prop('tab', item), uiReportFields)),
  )(tabs);
};

const getCloseExpandedContainerArg = () => {
  const routePath = G.getWindowLocationPathname();

  return R.includes(routePath, [GC.ROUTE_PATH_DISPATCH_BOARD_TEL, GC.ROUTE_PATH_DISPATCH_BOARD_CLO]) ?
    true : null;
};

export {
  getTabsSettings,
  getAllAllowedTabs,
  sortAndFilterTabs,
  getAllDefaultTabsValues,
  getCreateOrUpdateItemModal,
  getCloseExpandedContainerArg,
  RemainingBalanceWarningsConfirm,
  getDeactivationRemainingBalanceWarnings,
  getActivationProcessRemainingBalanceWarnings,
};
